import React, { useState, useEffect, useReducer, useContext, useCallback } from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";

import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import VisibilityIcon from "@material-ui/icons/Visibility";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import UserStatusIcon from "../../components/UserModal/statusIcon";

import moment from "moment";

import { listUsers, deleteUser } from "../../services/users.api";
import { listRoles } from "../../services/access.api";
import { getAccessSnapshot } from "../../context/AccessControl/accessSnapshot";
// Única fuente de la etiqueta del transporte: el mismo switch que usa la tarjeta
// de conexión y el desplegable del encabezado.
import { getWhatsAppProviderLabel } from "../../constants/whatsappProviders";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import UserModal from "../../components/UserModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { SocketContext, SocketManager } from "../../context/Socket/SocketContext";
import { Can } from "../../components/Can";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useAuditMode } from "../../context/AuditMode/AuditModeContext";
import { useHistory } from "react-router-dom";



const reducer = (state, action) => {
  if (action.type === "LOAD_USERS") {
    const users = action.payload;
    const newUsers = [];

    users.forEach((user) => {
      const userIndex = state.findIndex((u) => u.id === user.id);
      if (userIndex !== -1) {
        state[userIndex] = user;
      } else {
        newUsers.push(user);
      }
    });

    return [...state, ...newUsers];
  }

  if (action.type === "UPDATE_USERS") {
    const user = action.payload;
    const userIndex = state.findIndex((u) => u.id === user.id);

    if (userIndex !== -1) {
      // MEZCLA, no sustitución: el evento de socket transporta una vista PARCIAL
      // del usuario (la que serializa el servicio que hizo el cambio), no la fila
      // completa del listado. Sustituir borraba los campos ausentes —`online`,
      // `lastPresenceAt`, `roleId`— y la fila se degradaba hasta recargar.
      state[userIndex] = { ...state[userIndex], ...user };
      return [...state];
    } else {
      return [user, ...state];
    }
  }

  if (action.type === "DELETE_USER") {
    const userId = action.payload;

    const userIndex = state.findIndex((u) => u.id === userId);
    if (userIndex !== -1) {
      state.splice(userIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }

  if (action.type === "USER_PRESENCE") {
    const { id, online, lastPresenceAt } = action.payload;
    const uid = Number(id);
    const userIndex = state.findIndex((u) => Number(u.id) === uid);
    if (userIndex === -1) {
      return state;
    }
    const next = [...state];
    next[userIndex] = {
      ...next[userIndex],
      online,
      ...(lastPresenceAt !== undefined && lastPresenceAt !== null
        ? { lastPresenceAt }
        : {})
    };
    return next;
  }

  if (action.type === "MERGE_ONLINE_FROM_SERVER") {
    const fresh = action.payload;
    if (!Array.isArray(fresh) || fresh.length === 0) return state;
    const map = new Map(fresh.map((u) => [Number(u.id), u]));
    return state.map((u) => {
      const f = map.get(Number(u.id));
      if (!f) return u;
      return {
        ...u,
        online: Boolean(f.online),
        ...(f.lastPresenceAt !== undefined && f.lastPresenceAt !== null
          ? { lastPresenceAt: f.lastPresenceAt }
          : {}),
      };
    });
  }

  return state;
};

const useStyles = makeStyles((theme) => ({
  // ✅ CORREGIDO: MainPaper dinámico según tema
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
    borderRadius: '12px',
    boxShadow: theme.mode === 'dark' ? '0 4px 12px rgba(0, 0, 0, 0.3)' : '0 4px 12px rgba(0, 0, 0, 0.1)',
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e0e0e0'}`,
  },
  departmentContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
    justifyContent: 'center',
    maxWidth: '200px',
    alignItems: 'flex-start',
    margin: '0 auto',
    width: '100%',
    maxHeight: '60px',
    overflow: 'hidden',
    position: 'relative',
  },
  departmentChip: {
    backgroundColor: '#1976d2',
    color: 'white',
    padding: '3px 6px',
    borderRadius: '10px',
    fontSize: '10px',
    fontWeight: 'bold',
    maxWidth: '100%',
    wordBreak: 'break-word',
    whiteSpace: 'normal',
    lineHeight: '1.2',
    textAlign: 'center',
    display: 'inline-block',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    margin: '1px',
  },
  connectionCell: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '2px',
    lineHeight: 1.2,
  },
  connectionName: { fontSize: '13px', fontWeight: 'bold' },
  connectionProvider: { fontSize: '10px', opacity: 0.7 },
  // Heredada del equipo: se distingue de la propia porque cambia sola si
  // alguien toca los departamentos del equipo.
  connectionInherited: { fontSize: '13px', fontWeight: 500, fontStyle: 'italic', opacity: 0.85 },
  connectionOk: {
    fontSize: '10px',
    fontWeight: 'bold',
    color: '#fff',
    backgroundColor: '#2e7d32',
    padding: '1px 8px',
    borderRadius: '10px',
  },
  connectionOff: {
    fontSize: '10px',
    fontWeight: 'bold',
    color: '#fff',
    backgroundColor: '#c62828',
    padding: '1px 8px',
    borderRadius: '10px',
  },
  departmentOverflow: {
    position: 'absolute',
    bottom: '0',
    right: '0',
    backgroundColor: theme.mode === 'dark' ? '#6b7280' : '#666',
    color: 'white',
    padding: '2px 4px',
    borderRadius: '8px',
    fontSize: '9px',
    fontWeight: 'bold',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
  },
  actionButtons: {
    display: 'flex',
    gap: '8px',
    justifyContent: 'center',
    alignItems: 'center',
  },
  actionButton: {
    minWidth: '36px',
    height: '36px',
    borderRadius: '8px',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
    },
  },
  editButton: {
    backgroundColor: '#1976d2',
    color: 'white',
    '&:hover': {
      backgroundColor: '#1565c0',
    },
  },
  deleteButton: {
    backgroundColor: '#f44336',
    color: 'white',
    '&:hover': {
      backgroundColor: '#d32f2f',
    },
  },
  addButton: {
    borderRadius: '8px',
    padding: '8px 16px',
    fontWeight: 'bold',
    textTransform: 'none',
    boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)',
    '&:hover': {
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 12px rgba(25, 118, 210, 0.4)',
    },
  },
  // ✅ CORREGIDO: TableHeader dinámico según tema
  tableHeader: {
    backgroundColor: theme.mode === 'dark' ? '#374151' : '#f5f5f5',
    fontWeight: 'bold',
    fontSize: '14px',
    color: theme.palette.text.primary,
    borderBottom: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e0e0e0'}`,
  },
  // ✅ CORREGIDO: TableRow dinámico según tema
  tableRow: {
    backgroundColor: theme.palette.background.paper,
    '&:hover': {
      backgroundColor: theme.mode === 'dark' ? '#374151' : '#f8f9fa',
    },
    transition: 'background-color 0.2s ease',
    '& td': {
      color: theme.palette.text.primary,
      borderBottom: `1px solid ${theme.mode === 'dark' ? '#4B5563' : 'rgba(224, 224, 224, 0.5)'}`,
    },
  },
  profileBadge: {
    padding: '4px 12px',
    borderRadius: '16px',
    fontSize: '12px',
    fontWeight: 'bold',
    textTransform: 'uppercase',
  },
  statusCell: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(0.75),
  },
  lastPresenceOnline: {
    color: '#4caf50',
    fontWeight: 600,
    fontSize: '13px',
  },
  adminBadge: {
    backgroundColor: '#4caf50',
    color: 'white',
  },
  userBadge: {
    backgroundColor: '#2196f3',
    color: 'white',
  },
}));

const Users = () => {
  const classes = useStyles();

  const [loading, setLoading] = useState(false);

  // La columna «Perfil» mostraba el campo legacy `profile` (admin/user), que no
  // conoce los roles: la misma persona salia como USER aqui y como Supervisor en
  // Subagrupaciones. Ahora muestra el ROL, y `profile` queda como dato derivado.
  const [rolesById, setRolesById] = useState({});

  useEffect(() => {
    let activo = true;
    (async () => {
      try {
        const { data } = await listRoles();
        if (!activo) return;
        setRolesById(
          (data || []).reduce((acc, r) => ({ ...acc, [r.id]: r }), {})
        );
      } catch (err) {
        // Sin catalogo, la columna cae al valor legacy: se degrada, no se rompe.
        if (activo) setRolesById({});
      }
    })();
    return () => { activo = false; };
  }, []);
  // ¿La empresa trabaja por equipos? Decide qué departamentos se muestran.
  const modoEquipos = getAccessSnapshot().teamsEnabled === true;

  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [users, dispatch] = useReducer(reducer, []);

  const socketManager = useContext(SocketContext);
  const { user: loggedInUser } = useContext(AuthContext);
  const history = useHistory();
  const { startAudit } = useAuditMode();

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchUsers = async () => {
        try {
          const { data } = await listUsers({ searchParam, pageNumber });
          dispatch({ type: "LOAD_USERS", payload: data.users });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
        }
      };
      fetchUsers();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber]);

  const handleUserSocket = useCallback(
    (data) => {
      if (!data) return;
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_USERS", payload: data.user });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_USER", payload: +data.userId });
      }
    },
    []
  );

  const handleUserPresence = useCallback(
    (payload) => {
      if (!payload || payload.id == null) return;
      const mine =
        loggedInUser?.companyId != null && loggedInUser?.companyId !== ""
          ? Number(loggedInUser.companyId)
          : Number(localStorage.getItem("companyId"));
      if (
        payload.companyId != null &&
        Number.isFinite(mine) &&
        Number(payload.companyId) !== mine
      ) {
        return;
      }
      dispatch({
        type: "USER_PRESENCE",
        payload: {
          id: payload.id,
          online: Boolean(payload.online),
          lastPresenceAt: payload.lastPresenceAt
        }
      });
    },
    [loggedInUser?.companyId]
  );

  useEffect(() => {
    const companyId =
      loggedInUser?.companyId != null && loggedInUser?.companyId !== ""
        ? String(loggedInUser.companyId)
        : localStorage.getItem("companyId");
    if (companyId == null || companyId === "") {
      return undefined;
    }

    const socket = socketManager.getSocket(companyId);
    const evUser = `company-${companyId}-user`;

    socket.off(evUser, handleUserSocket);
    socket.on(evUser, handleUserSocket);
    // Evento fijo (backend libs/socket emitUserPresence) — misma cadena en todos los clientes
    socket.off("user-presence", handleUserPresence);
    socket.on("user-presence", handleUserPresence);

    return () => {
      if (socket && typeof socket.off === "function") {
        socket.off(evUser, handleUserSocket);
        socket.off("user-presence", handleUserPresence);
      }
    };
  }, [socketManager, loggedInUser?.companyId, handleUserSocket, handleUserPresence]);

  /** M6: tras reconexión engine.io, volver a leer online/lastPresence desde API (evita UI "pegada" en offline) */
  useEffect(() => {
    const companyId =
      loggedInUser?.companyId != null && loggedInUser?.companyId !== ""
        ? String(loggedInUser.companyId)
        : localStorage.getItem("companyId");
    if (companyId == null || companyId === "") {
      return undefined;
    }
    const syncPresence = async () => {
      try {
        const { data } = await listUsers({ searchParam, pageNumber: 1 });
        dispatch({ type: "MERGE_ONLINE_FROM_SERVER", payload: data.users });
      } catch (_) {
        /* evitar toast en background */
      }
    };
    return SocketManager.subscribeConnect(syncPresence);
  }, [searchParam, loggedInUser?.companyId]);

  const handleOpenUserModal = () => {
    setSelectedUser(null);
    setUserModalOpen(true);
  };

  const handleCloseUserModal = () => {
    setSelectedUser(null);
    setUserModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setUserModalOpen(true);
  };

  const handleStartAudit = async (targetUser) => {
    await startAudit(targetUser);
    history.push("/tickets");
  };

  const handleDeleteUser = async (userId) => {
    try {
      await deleteUser(userId);
      toast.success(i18n.t("users.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingUser(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          deletingUser &&
          `${i18n.t("users.confirmationModal.deleteTitle")} ${
            deletingUser.name
          }?`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteUser(deletingUser.id)}
      >
        {i18n.t("users.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <UserModal
        open={userModalOpen}
        onClose={handleCloseUserModal}
        aria-labelledby="form-dialog-title"
        userId={selectedUser && selectedUser.id}
        onSaved={() => {
          // Recarga completa: las columnas Departamento, Subagrupación y
          // Conexión se DERIVAN al listar y no viajan en el evento de socket.
          dispatch({ type: "RESET" });
          setPageNumber(1);
        }}
      />
      <MainHeader>
        <Title>{i18n.t("users.title")}</Title>
        <MainHeaderButtonsWrapper>
          <TextField
            placeholder={i18n.t("contacts.searchPlaceholder")}
            type="search"
            value={searchParam}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "gray" }} />
                </InputAdornment>
              ),
            }}
          />
          <Can
            role={loggedInUser.profile}
            perform="user-modal:editProfile"
            yes={() => (
              <Button
                variant="contained"
                color="primary"
                onClick={handleOpenUserModal}
                className={classes.addButton}
                startIcon={<EditIcon />}
              >
                {i18n.t("users.buttons.add")}
              </Button>
            )}
          />
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper
        className={classes.mainPaper}
        variant="outlined"
        onScroll={handleScroll}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
			<TableCell align="center" className={classes.tableHeader}>
                {i18n.t("users.table.id")}
              </TableCell>
              <TableCell align="center" className={classes.tableHeader}>{i18n.t("users.table.status")}</TableCell>
              <TableCell align="center" className={classes.tableHeader}>{i18n.t("users.table.name")}</TableCell>
              <TableCell align="center" className={classes.tableHeader}>
                {i18n.t("users.table.email")}
              </TableCell>
              <TableCell align="center" className={classes.tableHeader}>
                {i18n.t("users.table.profile")}
              </TableCell>
              <TableCell align="center" className={classes.tableHeader} style={{ width: '200px', minWidth: '200px' }}>
                {i18n.t("users.table.department")}
              </TableCell>
              <TableCell align="center" className={classes.tableHeader} style={{ width: '180px', minWidth: '180px' }}>
                Subagrupación
              </TableCell>
              <TableCell align="center" className={classes.tableHeader} style={{ width: '170px', minWidth: '170px' }}>
                {i18n.t("users.table.connection")}
              </TableCell>
              <TableCell align="center" className={classes.tableHeader}>
                {i18n.t("users.table.lastConnection")}
              </TableCell>
              <TableCell align="center" className={classes.tableHeader}>
                {i18n.t("users.table.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {users.map((user) => (
                <TableRow key={`user-row-${user.id}`} className={classes.tableRow}>
				  <TableCell align="center">{user.id}</TableCell>
				  <TableCell align="center">
                    <div className={classes.statusCell}>
                      <UserStatusIcon user={user} />
                      <span style={{ fontSize: '13px' }}>
                        {user.online
                          ? i18n.t("users.table.connected")
                          : i18n.t("users.table.disconnected")}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell align="center" style={{ fontWeight: '500' }}>{user.name}</TableCell>
                  <TableCell align="center">{user.email}</TableCell>
                  <TableCell align="center">
                    <span className={`${classes.profileBadge} ${
                      user.profile === 'admin' ? classes.adminBadge : classes.userBadge
                    }`}>
                      {rolesById[user.roleId]?.name || user.profile}
                    </span>
                  </TableCell>
                  <TableCell align="center" style={{ width: '200px', minWidth: '200px' }}>
                    {(() => {
                      /**
                       * Con el modo equipos y equipo asignado, manda el equipo.
                       *
                       * Los individuales NO se borran —siguen en la base como
                       * respaldo para poder apagar el modo—, pero dejan de
                       * mostrarse: enseñar los tres departamentos viejos de
                       * alguien cuyo equipo atiende uno solo describe una
                       * realidad que ya no es.
                       */
                      const deLEquipo = modoEquipos && user.userGroups?.length > 0;
                      const departamentos = deLEquipo
                        ? user.teamQueues ?? []
                        : user.queues ?? [];
                      return departamentos.length > 0 ? (
                      <div className={classes.departmentContainer}>
                        {departamentos.slice(0, 3).map((queue, index) => (
                          <span
                            key={`user-${user.id}-queue-${queue.id || 'unknown'}-${index}`}
                            className={classes.departmentChip}
                            style={{
                              backgroundColor: queue.color || '#1976d2',
                            }}
                          >
                            {queue.name}
                          </span>
                        ))}
                        {departamentos.length > 3 && (
                          <span className={classes.departmentOverflow}>
                            +{departamentos.length - 3} más
                          </span>
                        )}
                      </div>
                      ) : (
                        <span style={{ fontSize: '12px' }}>Sin departamento</span>
                      );
                    })()}
                  </TableCell>
                  <TableCell align="center" style={{ width: '180px', minWidth: '180px' }}>
                    {user.userGroups && user.userGroups.length > 0 ? (
                      <div className={classes.departmentContainer}>
                        {user.userGroups.slice(0, 2).map((group) => (
                          <span
                            key={`user-${user.id}-group-${group.id}`}
                            className={classes.departmentChip}
                            style={{ backgroundColor: '#5c6bc0' }}
                            title={
                              /*
                                Antes decía el rol contextual del miembro. Desde
                                la decisión del 19-ago ese rol ya no se elige, así
                                que describía algo que la pantalla no permite
                                cambiar. Lo útil ahora es si supervisa o no.
                              */
                              group.isSupervisor
                                ? "Supervisa este equipo"
                                : "Miembro del equipo"
                            }
                          >
                            {group.name}
                          </span>
                        ))}
                        {user.userGroups.length > 2 && (
                          <span className={classes.departmentOverflow}>
                            +{user.userGroups.length - 2} más
                          </span>
                        )}
                      </div>
                    ) : (
                      <span style={{ fontSize: '12px' }}>Sin subagrupación</span>
                    )}
                  </TableCell>
                  {/*
                    Conexión predeterminada: por dónde saldrán los tickets que
                    esta persona cree a mano. Mismo formato que el desplegable de
                    conexiones del encabezado —nombre, transporte y estado— para
                    que se reconozca sin tener que aprender otra convención.
                  */}
                  <TableCell align="center">
                    {(() => {
                      /**
                       * Dos orígenes, y hay que distinguirlos.
                       *
                       * La propia (`Users.whatsappId`) es una preferencia
                       * explícita. La del equipo se DEDUCE: equipo →
                       * departamentos → conexiones (`WhatsappQueues`). No hay
                       * campo «conexión del equipo» a propósito: sería una
                       * tercera definición del mismo hecho.
                       *
                       * Se marca de dónde viene, porque «heredada» significa que
                       * cambia sola si alguien toca los departamentos.
                       */
                      const heredadas = user.teamConnections ?? [];
                      const conexion = user.connection ?? heredadas[0] ?? null;
                      const esHeredada = !user.connection && Boolean(conexion);

                      if (!conexion) {
                        return (
                          <span style={{ fontSize: '12px' }}>
                            {i18n.t("users.table.connectionNone")}
                          </span>
                        );
                      }

                      return (
                        <div className={classes.connectionCell}>
                          <span
                            className={
                              esHeredada
                                ? classes.connectionInherited
                                : classes.connectionName
                            }
                          >
                            {conexion.name}
                            {heredadas.length > 1 && ` +${heredadas.length - 1}`}
                          </span>
                          <span className={classes.connectionProvider}>
                            {esHeredada
                              ? i18n.t("users.table.connectionFromTeam")
                              : getWhatsAppProviderLabel(conexion.provider)}
                          </span>
                          <span
                            className={
                              conexion.status === "CONNECTED"
                                ? classes.connectionOk
                                : classes.connectionOff
                            }
                          >
                            {conexion.status === "CONNECTED"
                              ? "Conectado"
                              : "Desconectado"}
                          </span>
                        </div>
                      );
                    })()}
                  </TableCell>
                  <TableCell align="center" style={{ fontSize: '13px' }}>
                    {user.online ? (
                      <span className={classes.lastPresenceOnline}>
                        {i18n.t("users.table.lastConnectionNow")}
                      </span>
                    ) : user.lastPresenceAt ? (
                      moment(user.lastPresenceAt).format("DD/MM/YYYY HH:mm:ss")
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <div className={classes.actionButtons}>
                      {loggedInUser.profile === "admin" && user.id !== loggedInUser.id && (
                        <IconButton
                          size="small"
                          onClick={() => handleStartAudit(user)}
                          title={`Auditar panel de ${user.name}`}
                          style={{ color: "#1976d2" }}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        onClick={() => handleEditUser(user)}
                        className={`${classes.actionButton} ${classes.editButton}`}
                        title="Editar usuario"
                      >
                        <EditIcon />
                      </IconButton>

                      <IconButton
                        size="small"
                        onClick={(e) => {
                          if (user.id === 1) {
                            toast.error("❌ No se puede eliminar el Super Usuario Master (ID: 1). Esta es la cuenta de configuración por defecto del sistema.");
                            return;
                          }
                          setConfirmModalOpen(true);
                          setDeletingUser(user);
                        }}
                        className={`${classes.actionButton} ${classes.deleteButton}`}
                        title={user.id === 1 ? "No se puede eliminar el Super Usuario Master" : "Eliminar usuario"}
                        disabled={user.id === 1}
                        style={{
                          opacity: user.id === 1 ? 0.5 : 1,
                          cursor: user.id === 1 ? 'not-allowed' : 'pointer'
                        }}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton key="loading-skeleton" columns={10} />}
            </>
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default Users;
